import { Theme } from '@css-modules-theme/core'
import {
  Placement,
  Side,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'
import classNames from 'classnames'
import { FunctionComponent, useId, useRef, useState } from 'react'
import StringToReactNode from '../StringToReactNode/StringToReactNode'
import TXT from '@/messages'
import { segment } from '@/utils/tracking.utils'
import { TICKET_DESCRIPTION_OPENED } from '@/config/tracking.config'
import { SegmentTicketDescriptionOpened } from '@/types/tracking.types'

export type NoteTooltipProps = {
  /** the description to reveal */
  note: string
  /** tier title, used for the accessible labels */
  title: string
  /** test id of the owning order item */
  dataTestId: string
  /** ticket category the tier belongs to, for tracking */
  category: Pick<Transformed.TicketCategory, 'id' | 'title'>
  /** composed OrderItem theme */
  styles: Theme
}

/** gap between the (i) icon and the tooltip, in px */
const TOOLTIP_OFFSET = 10

/** keeps the tooltip this far from the edge of the viewport when it has to slide */
const VIEWPORT_PADDING = 16

/**
 * Stops the arrow before it reaches a rounded corner, where it would cut across the
 * curve and leave a jagged notch. Needs to clear the 0.75rem border-radius plus the
 * ~2px each side that the 0.625rem square gains from being rotated 45°.
 */
const ARROW_PADDING = 16

/**
 * The edge of the tooltip the arrow is pinned to, per resolved placement. Keyed by the
 * whole placement rather than its side so the lookup needs no cast, and so a placement
 * Floating UI adds later is a compile error rather than an undefined edge.
 */
const ARROW_STATIC_SIDE: Record<Placement, Side> = {
  top: 'bottom',
  'top-start': 'bottom',
  'top-end': 'bottom',
  bottom: 'top',
  'bottom-start': 'top',
  'bottom-end': 'top',
  left: 'right',
  'left-start': 'right',
  'left-end': 'right',
  right: 'left',
  'right-start': 'left',
  'right-end': 'left',
}

/**
 * Reports the description being revealed, however it was revealed — hover, keyboard focus
 * or a click. Callers must fire this only on the transition into open: a mouse click is
 * always preceded by a hover, and both would otherwise report the same single reveal.
 */
const trackDescriptionOpened = (title: string, category: NoteTooltipProps['category']): void => {
  const payload: SegmentTicketDescriptionOpened = {
    /* eslint-disable camelcase -- Segment payload keys are snake_case, as in formatTicketsForTracking */
    ticket_type: title,
    category_id: category.id,
    category_title: category.title,
    /* eslint-enable camelcase */
  }

  segment.track(TICKET_DESCRIPTION_OPENED, payload)
}

/**
 * The (i) icon next to a ticket title and the tooltip it reveals.
 *
 * Opens on hover (mouse only) or keyboard focus transiently, and latches open when the
 * icon is clicked so it survives the pointer leaving. Closes on the close button, a
 * second click of the icon, Escape, or a click outside.
 *
 * Placement is delegated to Floating UI. `shift` is the part that matters on narrow
 * screens: the tooltip is wider than the space on either side of the icon, so it has to
 * slide along the viewport rather than flip, which is why this cannot be done in CSS.
 */
const NoteTooltip: FunctionComponent<NoteTooltipProps> = ({
  note,
  title,
  dataTestId,
  category,
  styles,
}: NoteTooltipProps) => {
  const tooltipId = useId()
  /** latched open by clicking the (i) icon — survives the pointer leaving */
  const [isOpen, setIsOpen] = useState(false)
  /** transiently open while hovered or focused */
  const [isHovered, setIsHovered] = useState(false)
  const isVisible = isOpen || isHovered
  /*
   * The same flags, readable before the next render. One mouse click delivers hover, focus and
   * click back to back, often before React re-renders, so the reveal check reads these rather
   * than `isVisible` — otherwise each of those handlers would see the tooltip as still hidden
   * and report the same reveal again.
   */
  const isOpenRef = useRef(false)
  const isHoveredRef = useRef(false)

  const setOpen = (next: boolean): void => {
    isOpenRef.current = next
    setIsOpen(next)
  }

  const setHovered = (next: boolean): void => {
    isHoveredRef.current = next
    setIsHovered(next)
  }

  const isRevealed = (): boolean => isOpenRef.current || isHoveredRef.current

  /** shows the tooltip on hover or focus, tracking it only if nothing had revealed it yet */
  const reveal = (): void => {
    if (!isRevealed()) trackDescriptionOpened(title, category)
    setHovered(true)
  }
  /*
   * Floating UI's elements are held as state rather than refs so that resolving them
   * stays a render-time value the linter is happy with, and so the middleware re-runs
   * when the arrow mounts.
   */
  const [referenceEl, setReferenceEl] = useState<HTMLButtonElement | null>(null)
  const [floatingEl, setFloatingEl] = useState<HTMLDivElement | null>(null)
  const [arrowEl, setArrowEl] = useState<HTMLSpanElement | null>(null)

  const close = (): void => {
    setOpen(false)
    setHovered(false)
  }

  const { floatingStyles, context, middlewareData, placement } = useFloating({
    elements: { reference: referenceEl, floating: floatingEl },
    open: isVisible,
    onOpenChange: (nextOpen) => {
      if (!nextOpen) close()
    },
    placement: 'top-start',
    middleware: [
      offset(TOOLTIP_OFFSET),
      flip(),
      shift({ padding: VIEWPORT_PADDING }),
      arrow({ element: arrowEl, padding: ARROW_PADDING }),
    ],
    whileElementsMounted: autoUpdate,
  })

  // Escape and outside-press handling, so the component does not hand-roll either
  const { getReferenceProps, getFloatingProps } = useInteractions([useDismiss(context)])

  const arrowSide = ARROW_STATIC_SIDE[placement]

  return (
    <div
      className={styles.NoteTooltipWrapper}
      data-testid={`${dataTestId}-note-tooltip-wrapper`}
      // hover is a mouse-only affordance: on touch, `pointerenter` fires alongside the
      // tap that toggles `isOpen`, which would make the tooltip impossible to close
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') reveal()
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'mouse') setHovered(false)
      }}
      // fires again when focus moves to the tooltip's close button; `reveal` doesn't track
      // that, since it is the same reveal, not a new one
      onFocus={reveal}
      onBlur={(event) => {
        // keep it open while focus moves between the (i) icon and the tooltip's close button
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setHovered(false)
        }
      }}
    >
      <button
        {...getReferenceProps({
          'aria-controls': tooltipId,
          'aria-expanded': isVisible,
          'aria-label': isVisible
            ? TXT.orderItem.hideDescription(title)
            : TXT.orderItem.showDescription(title),
          className: classNames('btn-unstyled', styles.NoteTooltipTrigger),
          type: 'button',
        })}
        data-testid={`${dataTestId}-note-tooltip-trigger`}
        // clicking latches the tooltip open independently of hover, so it survives
        // the pointer leaving; hover alone only shows it transiently. Set here rather than
        // through getReferenceProps, which is called during render and so can't take a
        // handler that reads refs; useDismiss doesn't set onClick on the reference.
        onClick={() => {
          /*
           * Gated on hover as well as the latch: a mouse has already revealed the tooltip by
           * hovering on its way to the click, and the click that latches that reveal open is
           * not a second reveal. This only fires for a click that reveals the tooltip
           * outright, as touch does.
           */
          if (!isRevealed()) trackDescriptionOpened(title, category)

          setOpen(!isOpenRef.current)
        }}
        ref={setReferenceEl}
      >
        {/* drawn rather than typeset: a text "i" is centred on its baseline, not on the
            circle, and renders differently per font/weight */}
        <svg aria-hidden="true" focusable="false" viewBox="0 0 16 16">
          <circle cx="8" cy="8" fill="currentColor" r="8" />
          <g transform="translate(6 3)">
            <circle cx="2.00008" cy="1.4395" r="1.4395" fill="white" />
            <path d="M3.81498 8.84656H3.61532C3.41107 8.84656 3.24528 8.68425 3.24528 8.48389V3.5768C3.24528 3.51023 3.20977 3.44834 3.15138 3.41386C3.09349 3.37936 3.02113 3.37775 2.96109 3.4087L0.437499 4.78325C-0.296821 5.143 0.0554512 5.5727 0.328946 5.56191C0.60228 5.5508 1.41651 5.4723 1.41651 5.4723V8.48393C1.41651 8.68429 1.25074 8.8466 1.04649 8.8466H0.654582C0.552467 8.8466 0.46958 8.92783 0.46958 9.02791V9.81882C0.46958 9.91877 0.552467 10 0.654582 10H3.81498C3.91711 10 4 9.91877 4 9.81882V9.02791C4.00002 8.92779 3.91711 8.84656 3.81498 8.84656Z" fill="white" />
          </g>
        </svg>
      </button>
      {isVisible && (
        <div
          {...getFloatingProps({
            className: styles.NoteTooltip,
            id: tooltipId,
            role: 'tooltip',
            style: floatingStyles,
          })}
          data-placement={placement}
          data-testid={`${dataTestId}-note-tooltip`}
          ref={setFloatingEl}
        >
          <StringToReactNode
            className={classNames(styles.NoteContainer, styles.NoteTooltipContent)}
            data-testid={`${dataTestId}-note`}
            incomingString={note}
            useTypoStandard={false}
          />
          <button
            aria-label={TXT.orderItem.hideDescription(title)}
            className={classNames('btn-unstyled', styles.NoteTooltipClose)}
            data-testid={`${dataTestId}-note-tooltip-close`}
            onClick={close}
            type="button"
          >
            <svg aria-hidden="true" focusable="false" viewBox="0 0 20 20">
              <circle cx="10" cy="10" fill="none" r="9" stroke="currentColor" strokeWidth="1.25" />
              <path
                d="M7 7 L13 13 M13 7 L7 13"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="1.5"
              />
            </svg>
          </button>
          <span
            className={styles.NoteTooltipArrow}
            ref={setArrowEl}
            style={{
              left: middlewareData.arrow?.x != null ? `${middlewareData.arrow.x}px` : '',
              top: middlewareData.arrow?.y != null ? `${middlewareData.arrow.y}px` : '',
              [arrowSide]: '-0.3125rem',
            }}
          />
        </div>
      )}
    </div>
  )
}

export default NoteTooltip
